import React, { useEffect, useState, useCallback } from "react";
import { Send, MessageCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * WorkoutCommentsThread — threaded back-and-forth between coach and athlete
 * scoped to a single workout in a program week.
 *
 * Used from BOTH sides:
 *   - Athlete portal: from inside the workout log modal.
 *   - Coach planner / detail: from inside the workout editor / log override.
 *
 * Props:
 *   side: "coach" | "athlete"
 *   athleteId: required when side === "coach" (which athlete the thread belongs to)
 *   programId / weekNumber / dayLabel / workoutId: thread key
 *   listFn, postFn: caller-supplied (the bound API helpers — see /app/frontend/src/lib/api.js)
 *
 * The component owns its own fetch + post lifecycle so callers don't need
 * to thread state through their own forms.
 */
export default function WorkoutCommentsThread({
  side,
  athleteId,
  programId,
  weekNumber,
  dayLabel,
  workoutId,
  listFn,
  postFn,
}) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const rows =
        side === "coach"
          ? await listFn(athleteId, programId, weekNumber, dayLabel, workoutId)
          : await listFn(programId, weekNumber, dayLabel, workoutId);
      setComments(rows || []);
    } catch (e) {
      // No toast — this is an inline panel; a quiet empty state is fine.
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [side, athleteId, programId, weekNumber, dayLabel, workoutId, listFn]);

  useEffect(() => {
    load();
  }, [load]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if (!text || posting) return;
    setPosting(true);
    try {
      const payload = {
        program_id: programId,
        week_number: weekNumber,
        day_label: dayLabel,
        workout_id: workoutId,
        body: text,
      };
      const saved =
        side === "coach"
          ? await postFn(athleteId, payload)
          : await postFn(payload);
      setComments((prev) => [...prev, saved]);
      setDraft("");
      toast.success(
        side === "coach"
          ? "Reply sent — your athlete will get an email"
          : "Reply sent to your coach"
      );
    } catch {
      toast.error("Couldn't send — please try again");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div
      className="mt-3 border-t border-[var(--tir-border)] pt-3"
      data-testid="workout-comments-thread"
    >
      <div className="flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-2">
        <MessageCircle size={11} />
        Conversation
        {comments.length > 0 && (
          <span className="font-mono-tir text-[10px] text-[var(--tir-burgundy)] normal-case tracking-normal">
            · {comments.length}
          </span>
        )}
      </div>
      <div className="space-y-2 mb-3 max-h-64 overflow-y-auto pr-1">
        {loading ? (
          <div className="text-[11px] italic text-[var(--tir-muted)]">Loading…</div>
        ) : comments.length === 0 ? (
          <div className="text-[11px] italic text-[var(--tir-muted)]">
            {side === "coach"
              ? "No replies yet — drop a quick note to keep them motivated."
              : "Your coach hasn't replied yet. Leave a question or update anytime."}
          </div>
        ) : (
          comments.map((c) => {
            const isCoach = c.from_role === "coach";
            // Align coach messages right when athlete is viewing, left when
            // coach is viewing — so "you" always sits on the right.
            const isYou = (side === "coach" && isCoach) || (side === "athlete" && !isCoach);
            return (
              <div
                key={c.id}
                className={`flex ${isYou ? "justify-end" : "justify-start"}`}
                data-testid={`comment-row-${c.id}`}
              >
                <div
                  className={`max-w-[85%] rounded-md px-3 py-2 ${
                    isCoach
                      ? "bg-[#FAF7F2] border border-[var(--tir-burgundy)]/20"
                      : "bg-white border border-[var(--tir-border)]"
                  }`}
                >
                  <div className="flex items-baseline gap-2 mb-1">
                    <span
                      className={`text-[10px] tracking-[0.18em] uppercase font-semibold ${
                        isCoach ? "text-[var(--tir-burgundy)]" : "text-[var(--tir-navy)]"
                      }`}
                    >
                      {isCoach ? "Coach" : "Athlete"} · {c.from_name}
                    </span>
                    {c.created_at && (
                      <span className="text-[9px] font-mono-tir text-[var(--tir-muted)]">
                        {new Date(c.created_at).toLocaleString(undefined, {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    )}
                  </div>
                  <div className="text-[12px] text-[var(--tir-ink)] whitespace-pre-wrap leading-snug">
                    {c.body}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      <form onSubmit={send} className="flex gap-2 items-start">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          placeholder={
            side === "coach"
              ? "Reply to your athlete… (they'll get an email)"
              : "Ask your coach or share a note…"
          }
          data-testid="comment-draft-input"
          className="flex-1 border border-[var(--tir-border)] bg-white px-2 py-1.5 text-[12px] focus:outline-none focus:border-[var(--tir-burgundy)] rounded-sm leading-snug resize-y"
        />
        <button
          type="submit"
          disabled={!draft.trim() || posting}
          data-testid="comment-send-btn"
          className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase font-semibold px-3 py-1.5 bg-[var(--tir-burgundy)] text-white hover:bg-[var(--tir-burgundy-dark)] disabled:opacity-40 disabled:cursor-not-allowed rounded-sm"
        >
          <Send size={11} /> Send
        </button>
      </form>
    </div>
  );
}
